import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ServerBanner from './components/ServerBanner';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import IntegrityGuard from './components/IntegrityGuard';
import Banned from './pages/Banned';
import useServerVerify from './hooks/useServerVerify';
import './App.css';

// Retry lazy imports on chunk load failure (stale cache after deploy)
const lazyRetry = (fn) => lazy(() => fn().catch(() => {
  window.location.reload();
  return new Promise(() => {}); // never resolves — page reloads
}));

const Landing          = lazyRetry(() => import('./pages/Landing'));

const Login          = lazyRetry(() => import('./pages/Login'));
const AuthCallback   = lazyRetry(() => import('./pages/AuthCallback'));
const Dashboard      = lazyRetry(() => import('./pages/Dashboard'));
const Results        = lazyRetry(() => import('./pages/Results'));
const Settings       = lazyRetry(() => import('./pages/Settings'));
const CustomStrings  = lazyRetry(() => import('./pages/CustomStringss'));
const Admin          = lazyRetry(() => import('./pages/Admin'));
const Enterprise     = lazyRetry(() => import('./pages/Enterprise'));
const Support        = lazyRetry(() => import('./pages/Support'));
const Tickets        = lazyRetry(() => import('./pages/Tickets'));
const PricingNew     = lazyRetry(() => import('./pages/PricingNew'));
const Pricing        = lazyRetry(() => import('./pages/Pricing'));
const FreeTools      = lazyRetry(() => import('./pages/FreeTools'));
const PublicResults  = lazyRetry(() => import('./pages/PublicResults'));
const ResultsPinEntry = lazyRetry(() => import('./pages/ResultsPinEntry'));
const ResultsView    = lazyRetry(() => import('./pages/ResultsView'));
const Download       = lazyRetry(() => import('./pages/Download'));
const ChangelogManager = lazyRetry(() => import('./pages/ChangelogManager'));
const Changelogs = lazyRetry(() => import('./pages/Changelogs'));
const LandingDocumentation = lazyRetry(() => import('./landing/LandingSite').then(m => ({ default: m.LandingDocumentation })));
const LandingLegalByPath = lazyRetry(() => import('./landing/LandingSite').then(m => ({ default: m.LandingLegalByPath })));
const AdminStats       = lazyRetry(() => import('./pages/AdminStats'));
const AdminCalendar    = lazyRetry(() => import('./pages/AdminCalendar'));
const AdminIPTracker   = lazyRetry(() => import('./pages/AdminIPTracker'));
const AdminMessages    = lazyRetry(() => import('./pages/AdminMessages'));
const AdminTickets     = lazyRetry(() => import('./pages/AdminTickets'));
const AdminSystem      = lazyRetry(() => import('./pages/AdminSystem'));
const AdminScanFinder  = lazyRetry(() => import('./pages/AdminScanFinder'));
const StaffChat        = lazyRetry(() => import('./pages/StaffChat'));
const TechCareers    = lazyRetry(() => import('./pages/TechCareers'));
const Analyzer       = lazyRetry(() => import('./pages/Analyzer'));
const Developer      = lazyRetry(() => import('./pages/Developer'));
const NoLicense      = lazyRetry(() => import('./pages/NoLicense'));

// ── Route Guards with Server-Side Verification ──
// Each guard makes an independent POST to /api/auth/verify.
// The response includes an HMAC _proof signed by the server secret.
// Overriding /auth/me or /settings in DevTools does NOT affect these guards
// because they query the database directly via a separate request.

const ProtectedRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  const { verified, verifying } = useServerVerify();
  if (loading || verifying) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!user) return <Navigate to="/login" />;
  if (!verified?._proof || !verified?.isAdmin) return <Navigate to="/dashboard" />;
  return children;
};

const LicenseRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  const { verified, verifying } = useServerVerify();
  if (loading || verifying) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!user) return <Navigate to="/login" />;
  if (!verified?._proof || !verified?.has_license) return <Navigate to="/pricing" />;
  return children;
};

const PlusRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  const { verified, verifying } = useServerVerify();
  if (loading || verifying) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!user) return <Navigate to="/login" />;
  if (!verified?._proof || !verified?.hasGVSecurityPlus) return <Navigate to="/pricing" />;
  return children;
};

const LicenseRequiredRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  const { verified, verifying, reverify } = useServerVerify();
  const [searchParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const isPaymentSuccess = searchParams.get('payment') === 'success';

  const hasAccess = verified?._proof && (verified.has_license || verified.hasEnterprise || verified.isEnterpriseMember || verified.isAdmin);

  const startPolling = useCallback(() => {
    if (!isPaymentSuccess || hasAccess) return;
    setPolling(true);
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        await reverify();
      } catch (_) { /* ignore */ }
      if (attempts >= 10) { clearInterval(iv); setPolling(false); }
    }, 3000);
    return () => clearInterval(iv);
  }, [isPaymentSuccess, hasAccess, reverify]);

  useEffect(() => startPolling(), [startPolling]);
  useEffect(() => { if (hasAccess) setPolling(false); }, [hasAccess]);

  if (loading || verifying) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!user) return <Navigate to="/login" />;
  if (polling) return <div className="loading" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'1rem' }}><div className="loading">Processing your payment...</div></div>;
  if (!verified?._proof) return <Navigate to="/no-license" />;
  if (!hasAccess) return <Navigate to="/no-license" />;
  return children;
};

const ChangelogManagerRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  const { verified, verifying } = useServerVerify();
  if (loading || verifying) return <div className="loading">Loading...</div>;
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!user) return <Navigate to="/login" />;
  if (!verified?._proof || !verified?.isAdmin) return <Navigate to="/dashboard" />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, banInfo } = useAuth();
  if (banInfo) return <Banned banInfo={banInfo} />;
  if (!loading && user) return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
        <div className="app">
          {/* <IntegrityGuard /> — disabled: HMAC verification has false positives from JSON re-serialization mismatches */}
          <MaintenanceOverlay />
          <ServerBanner />
          <Suspense fallback={<div className="loading">Loading…</div>}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/banned" element={<Banned />} />
            <Route path="/no-license" element={<NoLicense />} />
            <Route path="/dashboard" element={<LicenseRequiredRoute><Dashboard /></LicenseRequiredRoute>} />
            <Route path="/results" element={<LicenseRequiredRoute><Results /></LicenseRequiredRoute>} />
            <Route path="/results/view/:id" element={<LicenseRequiredRoute><ResultsView /></LicenseRequiredRoute>} />
            <Route path="/results/:id" element={<LicenseRequiredRoute><Results /></LicenseRequiredRoute>} />
            <Route path="/custom-strings" element={<LicenseRequiredRoute><CustomStrings /></LicenseRequiredRoute>} />
            <Route path="/newstrngs" element={<Navigate to="/custom-strings" replace />} />
            <Route path="/settings" element={<LicenseRequiredRoute><Settings /></LicenseRequiredRoute>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/admin/stats" element={<AdminRoute><AdminStats /></AdminRoute>} />
            <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
            <Route path="/admin/ip-tracker" element={<AdminRoute><AdminIPTracker /></AdminRoute>} />
            <Route path="/admin/messages" element={<AdminRoute><AdminMessages /></AdminRoute>} />
            <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
            <Route path="/admin/system" element={<AdminRoute><AdminSystem /></AdminRoute>} />
            <Route path="/admin/scan-finder" element={<AdminRoute><AdminScanFinder /></AdminRoute>} />
            <Route path="/admin/staff-chat" element={<AdminRoute><StaffChat /></AdminRoute>} />
            <Route path="/enterprise" element={<LicenseRequiredRoute><Enterprise /></LicenseRequiredRoute>} />
            <Route path="/terms-of-service" element={<LandingLegalByPath />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/eula" element={<LandingLegalByPath />} />
            <Route path="/privacy-policy" element={<LandingLegalByPath />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/refund-policy" element={<LandingLegalByPath />} />
            <Route path="/ethical-policy" element={<LandingLegalByPath />} />
            <Route path="/responsible-use" element={<Navigate to="/ethical-policy" replace />} />
            <Route path="/support" element={<Support />} />
            <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
            <Route path="/documentation" element={<LandingDocumentation />} />
            <Route path="/docs" element={<Navigate to="/documentation" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/plans" element={<Navigate to="/pricing" replace />} />
            <Route path="/price" element={<PricingNew />} />
            <Route path="/free-tools" element={<FreeTools />} />
            <Route path="/download" element={<Download />} />
            <Route path="/changelogs" element={<Changelogs />} />
            <Route path="/change-logs" element={<Navigate to="/changelogs" replace />} />
            <Route path="/admin/changelogs" element={<ChangelogManagerRoute><ChangelogManager /></ChangelogManagerRoute>} />
            <Route path="/results/pin" element={<ResultsPinEntry />} />
            <Route path="/results/pin/:pin" element={<PublicResults />} />
            <Route path="/scan/:pin" element={<PublicResults />} />
            <Route path="/results/:pin" element={<PublicResults />} />
            <Route path="/cgs" element={<TechCareers />} />
            <Route path="/analyzer" element={<LicenseRequiredRoute><Analyzer /></LicenseRequiredRoute>} />
            <Route path="/developer" element={<Developer />} />
            <Route path="/dev" element={<Navigate to="/developer" replace />} />
          </Routes>
          </Suspense>
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
